import { useCompleteProfileMutation } from '@streamster/shared/api/api.portal';
import { Me } from '@streamster/shared/api/types';
import mixins from '@streamster/shared/app/styles';
import Button from '@streamster/shared/components/Button/Button';
import { MaterialInput } from '#/components/MaterialInput/MaterialInput';
import handleLink from '@streamster/shared/helpers/handle-link';
import { useAppNavigation, useAppRoute } from '@streamster/shared/navigation/navigation.ref';
import { ToasterContext } from '@streamster/shared/toaster/toaster.provider';
import { getAppName } from '@streamster/shared/utils';
import React, { useContext, useState } from 'react';
import { Pressable, Text, View } from 'react-native';
import { CheckBox } from 'react-native-web';
import styles from './ProfileCompleteBox.styles';

type Props = { profile: Me };

export const ProfileCompleteBox: React.FC<Props> = ({ profile }) => {
  const [isReferralCodeVisible, setReferralCodeVisibility] = useState(false);
  const { mutateAsync: completeProfile, isPending } = useCompleteProfileMutation();
  const navigation = useAppNavigation();
  const route = useAppRoute<'Profile'>();
  const { error, success } = useContext(ToasterContext);
  const [fields, setFields] = useState({
    login: profile.login.split('#')?.at(-1) || '',
    password: '',
    confirmPassword: '',
    email: profile.email.split('#')?.at(-1) || '',
    referralCode: '',
    terms: false,
  });

  const setFieldValue = (field: keyof typeof fields) => (value: string | boolean | number) => {
    setFields(f => ({ ...f, [field]: value }));
  };

  const requiredFields: Array<keyof typeof fields> = [
    'email',
    'password',
    'login',
    'terms',
    'confirmPassword',
  ];

  const isFormValid =
    requiredFields.every(field =>
      typeof fields[field] === 'string' ? fields[field].trim().length > 0 : fields[field],
    ) && fields.password === fields.confirmPassword;

  const handleRegister = async () => {
    const { password, confirmPassword, terms } = fields;
    const chosenTariff = route.params?.chosenTariff;

    if (password !== confirmPassword) {
      return error({ message: "Passwords don't match" });
    }
    if (!terms) {
      return error({ message: 'Please accept our terms and conditions' });
    }
    if (!isFormValid) {
      return error({ message: 'Please fill all fields' });
    }

    await completeProfile(fields);

    if (chosenTariff) {
      success({ message: 'Profile completed, you can change your tariff now.' });
      navigation.navigate('Tariff', { chosenTariff });
    }
  };

  return (
    <View style={styles.webWrapper}>
      <Text style={styles.webHeader}>Create account</Text>
      <Text style={styles.webSubHeader}>to continue to {getAppName()}</Text>
      <View style={[styles.inputContainer, styles.webInputContainer]}>
        <MaterialInput
          onChangeText={setFieldValue('login')}
          value={fields.login}
          autoComplete="username"
          label="User name"
        />
        <MaterialInput
          onChangeText={setFieldValue('email')}
          value={fields.email}
          autoComplete="email"
          label="Email"
        />
        <MaterialInput
          secureTextEntry
          onChangeText={setFieldValue('password')}
          value={fields.password}
          autoComplete="password"
          label="Password"
        />
        <MaterialInput
          secureTextEntry
          onChangeText={setFieldValue('confirmPassword')}
          value={fields.confirmPassword}
          autoComplete="password"
          label="Confirm password"
        />
        {isReferralCodeVisible ? (
          <MaterialInput
            onChangeText={setFieldValue('referralCode')}
            value={fields.referralCode}
            label="Referral code"
          />
        ) : (
          <Pressable
            onPress={() => {
              setReferralCodeVisibility(true);
            }}
          >
            <Text style={styles.haveReferralCodeText}>Have a referral code?</Text>
          </Pressable>
        )}
        <View style={styles.termsWrapper}>
          <CheckBox
            style={styles.checkbox}
            color={mixins().color.blue}
            value={fields.terms}
            onValueChange={setFieldValue('terms')}
          />
          <Text style={styles.termsText} onPress={() => setFieldValue('terms')(!fields.terms)}>
            I agree to{' '}
            <Text
              style={styles.termsLink}
              onPress={() => handleLink('https://streamster.io/terms-conditions/')}
            >
              Terms & Conditions
            </Text>
          </Text>
        </View>
        <Button onPress={handleRegister} loading={isPending} style={styles.btnRegister}>
          Create
        </Button>
      </View>
    </View>
  );
};
